import { Seo } from "@/components/Seo";
import { Reveal, SectionTitle, Hairline, StatusBadge } from "@/components/primitives";
import { StrategicAssetsNav } from "@/components/StrategicAssetsNav";
import { InquiryForm } from "@/components/InquiryForm";

const FOCUS = [
  { t: "Title & Lien Review", d: "Recorded ownership, encumbrances, and liens establish the questions that must be resolved before a property-related position can be evaluated responsibly." },
  { t: "Market & Records Evidence", d: "An assessment of value draws on public records, authorized data, comparable transactions, and documented property condition, with the limits of that evidence made explicit." },
  { t: "Structure & Disclosure", d: "When assignment or wholesaling is relevant, the contractual structure, required disclosures, and transaction obligations must be identified before proceeding." },
];

const BEFORE = [
  "You have a real-estate matter, property, or opportunity you wish to discuss",
  "You are prepared to provide accurate property and contact information",
  "Timelines and outcomes vary based on market, jurisdiction, and structure",
  "Submission does not guarantee engagement or any transaction outcome",
];

export default function SARealEstate() {
  return (
    <div className="glow-navy jmh-page">
      <Seo title="Real Estate — Strategic Assets" description="Real Estate — a Strategic Assets capability (in development) covering real-estate acquisitions and wholesaling/assignment. Not a licensed brokerage. A Jennae Michelle Holdings portfolio company." />
      <StrategicAssetsNav />

      <section className="text-center jmh-section jmh-page-opening">
        <Reveal>
          <StatusBadge status="In development" className="mb-5" />
          <h1 className="font-serif2 text-4xl md:text-5xl text-[var(--bone)] leading-tight">Real Estate</h1>
          <p className="mt-2 font-display uppercase tracking-luxe text-[10px] text-[var(--muted)]">A Strategic Assets Capability · Jennae Michelle Holdings</p>
          <p className="mt-8 mx-auto font-serif2 text-xl md:text-2xl text-[var(--bone)]/90 leading-relaxed">
            Property decisions connect financial considerations with ownership, title, liens, condition, and market evidence. The intended review integrates those elements into a documented assessment that distinguishes established facts from unresolved questions. This capability remains in development.
          </p>
        </Reveal>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle tone="bone" testid="re-focus-title">Areas of Focus</SectionTitle></Reveal>
          <div className="mt-10 jmh-content-grid">
            {FOCUS.map((f, i) => (
              <Reveal key={f.t} delay={i * 0.05}>
                <div className="corner-frame px-7 py-8 h-full">
                  <h3 className="font-serif2 text-lg md:text-xl text-[var(--bone)]">{f.t}</h3>
                  <p className="mt-4 text-sm md:text-base text-[var(--bone)]/75 leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle tone="bone" testid="re-before-title">Before Proceeding</SectionTitle></Reveal>
          <ul className="mt-10 space-y-5">
            {BEFORE.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex gap-4 text-[var(--bone)]/90">
                  <span className="text-[var(--gold)] mt-1">•</span>
                  <span className="font-display uppercase tracking-wide2 text-xs md:text-sm leading-relaxed">{b}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Hairline className="my-12 opacity-60" />
        </div>
      </section>

      <section className="jmh-section">
        <div className="mx-auto jmh-content-shell">
          <Reveal><SectionTitle testid="re-initiate-title">Confidential Inquiry</SectionTitle></Reveal>
          <p className="mt-4 mb-10 text-center font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)]">This capability remains under development. An inquiry may be reviewed, but submission does not establish an engagement.</p>
          <Reveal delay={0.05}>
            <InquiryForm type="real_estate" division="Strategic Assets — Real Estate" cta="Submit a Confidential Inquiry" requireAck testid="re-form" note="All submissions are reviewed confidentially and at our discretion. Submission does not guarantee engagement or any transaction outcome." />
          </Reveal>
        </div>
      </section>

      <div className="pb-24 pt-4 text-center px-6">
        <p className="font-display uppercase tracking-wide2 text-[10px] md:text-xs text-[var(--muted)] leading-loose mx-auto">
          Real Estate is in development and is not currently accepting clients. Strategic Assets is not a licensed real-estate broker and does not provide brokerage, finder, legal, or investment services, and does not guarantee any transaction outcome.
        </p>
      </div>
    </div>
  );
}
